import { z } from 'zod';
import { ASK_VIEW_LOOP2_GAP_CODES } from './loop2-payload';
import type { AskViewAddress } from './view-payload';

/** Evidence crumbs consume the rail's published SG&A readiness; they never rediscover a source. */
export function drillEvidenceAddress(from: AskViewAddress, target: AskViewAddress,
  commitmentDrills: readonly AskViewAddress[]): AskViewAddress {
  if (from.viewId !== 'ev-it' || target.viewId !== 'ev-sga') return target;
  const tab = from.params.tab ?? 'buyer';
  const linked = commitmentDrills.find(address => address.viewId === 'ev-sga'
    && address.dealId === from.dealId && address.asOf === from.asOf
    && address.params.tab === tab && address.params.commitmentId === from.params.commitmentId);
  return linked ?? { dealId: from.dealId, asOf: from.asOf, viewId: 'ev-sga', params: { tab } };
}

const duplicateIdentity = ASK_VIEW_LOOP2_GAP_CODES.find(code => code === 'duplicate_identity')!;
/** Ordered precedence after authentication and authorization. Annotations never select an outcome. */
export const DRILL_OUTCOMES = ['revision_refused', 'hierarchy_undeclared', 'incomplete_periods', 'ambiguous_authority', 'role_ambiguous',
  'UNKNOWN_MEMBER', duplicateIdentity, 'overlapping_members', 'parent_unavailable',
  'empty_level', 'no_deeper_level', 'ready'] as const;
export type DrillOutcome = typeof DRILL_OUTCOMES[number];
export const DRILL_ANNOTATIONS = ['tied', 'signed_value', 'partial_children', 'unplaced_remainder',
  'incomplete_periods', 'unit_unavailable', 'insufficient_series'] as const;
export type DrillAnnotation = typeof DRILL_ANNOTATIONS[number];

/** Terminal depth is independent of record count and additive quality annotations. */
export function drillLevelState(input: Readonly<{ hasNextLevel: boolean; recordCount: number;
  unit?: string; periodComplete: boolean; trendRequested: boolean; seriesLength: number }>)
  : Readonly<{ outcome: DrillOutcome; annotations: readonly DrillAnnotation[] }> {
  const annotations: DrillAnnotation[] = [];
  if (!input.unit?.trim()) annotations.push('unit_unavailable');
  if (!input.periodComplete) annotations.push('incomplete_periods');
  if (input.trendRequested && input.seriesLength < 2) annotations.push('insufficient_series');
  return { outcome: !input.hasNextLevel ? 'no_deeper_level' : input.recordCount === 0 ? 'empty_level' : 'ready', annotations };
}
export const DRILL_PARAM_NAMES = ['dealId', 'asOf', 'tab', 'commitmentId', 'memberRef', 'dimensionRef',
  'measureRef', 'pins', 'role', 'period', 'level', 'unit', 'filter', 'cursor', 'area'] as const;
export type DrillTarget = AskViewAddress;
export type DrillPin = readonly [datasetRef: string, revisionId: string, digest: string];
export const DRILL_VIEW_IDS = ['ev-it', 'ev-sga'] as const;
export function isDrillViewId(viewId: AskViewAddress['viewId']): boolean {
  return DRILL_VIEW_IDS.some(id => id === viewId);
}
export const DRILL_PINS_MAX_LENGTH = 4096;
const text = z.string().min(1).max(512);
const pinSchema = z.tuple([text, z.string().regex(/^rt3rev_[a-f0-9]{32}$/),
  z.string().regex(/^[a-f0-9]{64}$/)]).readonly();
const pinsSchema = z.array(pinSchema).min(1).max(4).refine(entries =>
  new Set(entries.map(entry => entry[0])).size === entries.length, 'Duplicate dataset pin');

/** JSON is an external transport boundary. Publication identities are never selected by their spelling. */
export function readDrillPinSet(encoded: string): Readonly<{ pins: readonly DrillPin[] | null;
  code?: 'PIN_SET_TOO_LARGE' | 'revision_refused' }> {
  if (encoded.length > DRILL_PINS_MAX_LENGTH) return { pins: null, code: 'PIN_SET_TOO_LARGE' };
  try {
    const parsed = pinsSchema.safeParse(JSON.parse(encoded));
    if (!parsed.success) return { pins: null, code: parsed.error.issues.some(issue =>
      issue.code === 'too_big' && issue.path.length === 0) ? 'PIN_SET_TOO_LARGE' : 'revision_refused' };
    const sorted = [...parsed.data].sort((left, right) => left[0].localeCompare(right[0]));
    return JSON.stringify(sorted) === encoded ? { pins: sorted } : { pins: null, code: 'revision_refused' };
  } catch (cause: unknown) {
    if (cause instanceof SyntaxError) return { pins: null, code: 'revision_refused' };
    throw cause;
  }
}
export function readDrillPins(encoded: string): readonly DrillPin[] | null { return readDrillPinSet(encoded).pins; }

const paramsSchema = z.object({ tab: z.enum(['target', 'buyer']).optional(), commitmentId: text.optional(),
  memberRef: text, dimensionRef: text, measureRef: text,
  pins: z.string().max(DRILL_PINS_MAX_LENGTH).refine(value => readDrillPins(value) !== null), role: text, period: text, level: text,
  unit: text.optional(), filter: text.optional(), cursor: text.optional(), area: text.optional() }).strict();
export const drillTargetSchema: z.ZodType<DrillTarget> = z.object({ dealId: text,
  asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), viewId: z.enum(DRILL_VIEW_IDS),
  params: paramsSchema }).strict().refine(address => address.viewId !== 'ev-it' || !!address.params.commitmentId);

export function isDrillRefusal(outcome: DrillOutcome | undefined): boolean {
  return outcome !== undefined && outcome !== 'ready' && outcome !== 'empty_level' && outcome !== 'no_deeper_level';
}

/** Explicit refresh drops the cut and level, preserving only the entry's opaque legacy scope. */
export function drillRootAddress(address: AskViewAddress): AskViewAddress {
  return { ...address, params: Object.fromEntries(Object.entries(address.params)
    .filter(([name]) => name === 'tab' || name === 'commitmentId' || name === 'area')) };
}
